"use client";

import { useRef, useState } from "react";

import type { AppThemeTone } from "@/components/editorial/product-bridge/app-surface";

import { guidedMomentFor, type GuidedMoment } from "./moments";
import { AppPortalScope } from "./portal-scope";
import { ShotRecordSheet } from "./record-sheet";
import { ShotMobileRunTraceSheet, ShotRunTrace } from "./run-trace";
import { OSTREY_STORY, type ShotStory } from "./story";
import { ShotStoryTurn } from "./turn";
import { ShotAnswerFooter, ShotClosedTrace, ShotWindow } from "./window";

/** How the guided shot introduces itself to assistive technology. */
export const GUIDED_SHOT_LABEL =
  "Sample workspace. Open a sample's record link to see its record, or the run trace to see how the answer was made.";

/**
 * SES-007 · the static shot with its moments live. A record link opens that
 * record's sheet, with its photographs and its fields, stepping through the
 * records the answer links; the run trace pill opens the run's trace when
 * the story carries the run. The menu bar, rail, toolbar and composer stay
 * as they are but inert: nothing sends, types or navigates.
 */
export function GuidedProductShot({
  tone,
  initialMoment = null,
  story = OSTREY_STORY,
  label = GUIDED_SHOT_LABEL,
}: {
  tone: AppThemeTone;
  initialMoment?: GuidedMoment | null;
  story?: ShotStory;
  label?: string;
}) {
  const [recordId, setRecordId] = useState<string | null>(
    initialMoment?.kind === "record" ? initialMoment.recordId : null,
  );
  const [traceOpen, setTraceOpen] = useState(story.trace.openable && initialMoment?.kind === "trace");
  const opener = useRef<HTMLElement | null>(null);
  // The records the answer links are the sheet's previous and next.
  const recordIndex = story.records.findIndex((candidate) => candidate.id === recordId);
  const record = story.records[recordIndex] ?? null;

  const closeRecord = () => {
    setRecordId(null);
    opener.current?.focus();
  };

  return (
    <AppPortalScope tone={tone}>
      <ShotWindow
        tone={tone}
        workspace={story.workspace}
        chromeInert
        role="group"
        aria-label={label}
        onClickCapture={(event) => {
          const moment = guidedMomentFor(event.target);
          const link = event.target instanceof Element ? event.target.closest("a") : null;
          // Nothing in a shot navigates away; record links open their sheet.
          if (link) event.preventDefault();
          if (moment?.kind === "record") {
            event.stopPropagation();
            opener.current = link;
            setRecordId(moment.recordId);
          }
        }}
        transcript={<ShotStoryTurn story={story} />}
        footer={
          <ShotAnswerFooter
            trace={
              story.trace.openable ? (
                <ShotRunTrace open={traceOpen} onOpenChange={setTraceOpen} />
              ) : (
                <ShotClosedTrace label={story.trace.label} durationLabel={story.trace.durationLabel} updateCountLabel="" />
              )
            }
          />
        }
        overlay={
          <>
            {story.trace.openable ? (
              <ShotMobileRunTraceSheet open={traceOpen} onOpenChange={setTraceOpen} />
            ) : null}
            {record ? (
              <ShotRecordSheet
                record={record}
                onClose={closeRecord}
                navigation={story.records.length > 1 ? { index: recordIndex, total: story.records.length } : null}
                onNavigate={(direction) => {
                  const next = Math.min(Math.max(recordIndex + direction, 0), story.records.length - 1);
                  setRecordId(story.records[next]?.id ?? null);
                }}
              />
            ) : null}
          </>
        }
      />
    </AppPortalScope>
  );
}
